import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { always, compose, cond, includes, isNil, none, path, T } from 'ramda';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider, ShowOnlyOnPrint } from 'components';
import OutputTextField from 'components/atoms/GefMui/OutputTextField';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import { codelists } from 'constants/codelists';
import { TASK_TYPES } from 'features/views/taskDetail/constants';
import { getDetail } from 'features/views/taskDetail/selectors';
import { equalOrThreeDashes, getCodelistPropById, toNormalDate } from 'utils';

const { LOSE_COMISSION } = TASK_TYPES;

const PolicyInfoPrintOnly = ({ products, policyStatuses }) => {
	const taskDetail = useSelector(getDetail);

	const { taskTypeId, metadata = {}, claimNo, policy = {} } = taskDetail;
	const { policyNo, proposalNo, productLines, policyStatusId } = policy;
	const { common = {} } = metadata;

	const metaDataSource = [LOSE_COMISSION, 44, 46, 49, 50, 51, 52].includes(taskTypeId);

	const getProductLines = cond([
		[(params) => includes(params.taskTypeId, [LOSE_COMISSION]), (params) => path(['common', 'insurance'], params)],
		[
			(params) => none(isNil, [params.productLines, params.products]),
			(params) =>
				productLines.map((pL) => getCodelistPropById(pL, params.products, 'shortDescription')).join(', '),
		],
		[T, always('---')],
	]);

	const getPolicyStatus = cond([
		[(params) => includes(params.taskTypeId, [LOSE_COMISSION]), (params) => path(['policyStatus'], params.common)],
		[
			(params) => none(isNil, [params.policyStatuses, params.policyStatusId]),
			(params) => getCodelistPropById(params.policyStatusId, params.policyStatuses),
		],
		[T, always('---')],
	]);

	const dateNegotiationValue =
		!includes(taskTypeId, [LOSE_COMISSION]) && policyNo
			? equalOrThreeDashes(toNormalDate(policy.policyOpenDate))
			: metaDataSource
			  ? equalOrThreeDashes(toNormalDate(common.policyOpenDate))
			  : '---';

	const dateBeginningValue = includes(taskTypeId, [LOSE_COMISSION])
		? equalOrThreeDashes(toNormalDate(common.policyValueDate))
		: equalOrThreeDashes(toNormalDate(policy.policyValueDate));

	const dateEndValue = includes(taskTypeId, [LOSE_COMISSION])
		? equalOrThreeDashes(toNormalDate(common.policyFinalDate))
		: equalOrThreeDashes(toNormalDate(policy.policyFinalDate));

	return (
		<ShowOnlyOnPrint>
			<Paper sx={{ p: 3, borderRadius: 2 }}>
				<Stack spacing={2}>
					<SectionHeader text={getLabelMessage('taskDetailPrint:policyInfoHeader')} />
					<Grid container>
						<Grid size={12}>
							<Grid container spacing={2}>
								<Grid size={6}>
									<OutputTextField
										size="small"
										id="PSNumber"
										label={getLabelMessage('taskDetailPrint:PSNumber')}
										fullWidth
										value={equalOrThreeDashes(policyNo)}
										slotProps={{
											inputLabel: {
												shrink: !!equalOrThreeDashes(policyNo),
											},
										}}
									/>
								</Grid>
								<Grid size={6}>
									<OutputTextField
										size="small"
										id="proposalNumber"
										label={getLabelMessage('taskDetailPrint:proposalNumber')}
										fullWidth
										value={equalOrThreeDashes(proposalNo)}
										slotProps={{
											inputLabel: {
												shrink: !!equalOrThreeDashes(proposalNo),
											},
										}}
									/>
								</Grid>
								<Grid size={6}>
									<OutputTextField
										size="small"
										id="insurance"
										label={getLabelMessage('taskDetailPrint:insurance')}
										fullWidth
										value={getProductLines({ taskTypeId, products, productLines, common })}
										slotProps={{
											inputLabel: {
												shrink: !!getProductLines({
													taskTypeId,
													products,
													productLines,
													common,
												}),
											},
										}}
									/>
								</Grid>
								<Grid size={6}>
									<OutputTextField
										size="small"
										id="PSStatus"
										label={getLabelMessage('taskDetailPrint:PSStatus')}
										fullWidth
										value={getPolicyStatus({ taskTypeId, policyStatuses, policyStatusId, common })}
										slotProps={{
											inputLabel: {
												shrink: !!getPolicyStatus({
													taskTypeId,
													policyStatuses,
													policyStatusId,
													common,
												}),
											},
										}}
									/>
								</Grid>
								<Grid size={6}>
									<OutputTextField
										size="small"
										id="dateNegotiation"
										label={getLabelMessage('taskDetailPrint:dateNegotiation')}
										fullWidth
										value={dateNegotiationValue}
										slotProps={{
											inputLabel: {
												shrink: !!dateNegotiationValue,
											},
										}}
									/>
								</Grid>
								<Grid size={6}>
									<OutputTextField
										size="small"
										id="dateBegining"
										label={getLabelMessage('taskDetailPrint:dateBegining')}
										fullWidth
										value={dateBeginningValue}
										slotProps={{
											inputLabel: {
												shrink: !!dateBeginningValue,
											},
										}}
									/>
								</Grid>
								<Grid size={6}>
									<OutputTextField
										size="small"
										id="dateEnd"
										label={getLabelMessage('taskDetailPrint:dateEnd')}
										fullWidth
										value={dateEndValue}
										slotProps={{
											inputLabel: {
												shrink: !!dateEndValue,
											},
										}}
									/>
								</Grid>
								{!isNil(claimNo) && (
									<Grid size={6}>
										<OutputTextField
											size="small"
											id="claimNo"
											label={getLabelMessage('taskDetailPrint:claimNo')}
											fullWidth
											value={claimNo}
											slotProps={{
												inputLabel: {
													shrink: !!claimNo,
												},
											}}
										/>
									</Grid>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Stack>
			</Paper>
		</ShowOnlyOnPrint>
	);
};

PolicyInfoPrintOnly.propTypes = {
	policyStatuses: PropTypes.array,
	products: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.products, 'products'),
	codelistProvider(codelists.policyStatuses, 'policyStatuses'),
)(PolicyInfoPrintOnly);
